import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { X, ShoppingBag, ArrowLeft, ChevronRight, Loader } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { useCheckout } from '../hooks/useCheckout';
import { AddressForm, validateAddress } from '../components/AddressForm';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ─ Styles ─ */
const Page = styled.main`background:#FFFDF7; min-height:100vh; padding-top:90px;`;
const Wrap = styled.div`
  max-width:1100px; margin:0 auto; padding:40px 32px 120px;
  @media(max-width:600px){ padding:24px 16px 80px; }
`;
const Header = styled.div`
  display:flex; align-items:center; gap:14px; margin-bottom:48px;
  @media(max-width:600px){ margin-bottom:28px; }
`;
const H1 = styled.h1`font-family:'Cormorant Garamond',serif; font-size:clamp(1.6rem,3vw,2.2rem); font-weight:400; color:#1A1208; margin:0;`;
const Eyebrow = styled.p`font-family:'Raleway',serif; font-size:.65rem; letter-spacing:.2em; color:#C07020; margin:0 0 8px;`;

const Layout = styled.div`
  display:grid; grid-template-columns:1fr 320px; gap:48px; align-items:start;
  @media(max-width:860px){ grid-template-columns:1fr; gap:32px; }
`;

/* Item row */
const ItemRow = styled.div`
  display:grid; grid-template-columns:88px 1fr auto; gap:20px; align-items:center;
  padding:24px 0; border-bottom:1px solid rgba(26,18,8,.07);
  @media(max-width:500px){
    grid-template-columns:72px 1fr;
    grid-template-rows:auto auto;
    gap:12px 14px;
  }
`;
const ItemImg = styled.div`
  width:88px; height:80px; border-radius:12px; overflow:hidden; background:#F5EFE6;
  display:flex; align-items:center; justify-content:center;
  img{width:90%;height:90%;object-fit:contain;}
  @media(max-width:500px){ width:72px; height:66px; }
`;
const ItemName = styled(Link)`
  font-family:'Cormorant Garamond',serif; font-size:1.1rem; color:#1A1208; text-decoration:none;
  &:hover{color:#7C3A00;}
  @media(max-width:500px){ font-size:1rem; }
`;
const TypePill = styled.span`
  display:inline-block; margin-top:4px; padding:3px 12px; border-radius:999px;
  font-size:.6rem; letter-spacing:.1em;
  background:${({$v})=>$v==='envinada'?'#1A1208':'rgba(124,58,0,.08)'};
  color:${({$v})=>$v==='envinada'?'#FAF3E0':'#7C3A00'};
`;
const ItemPrice = styled.div`font-weight:700; color:#7C3A00; font-size:.95rem; text-align:right;`;
const ItemSubtotal = styled.div`font-size:.78rem; color:rgba(26,18,8,.45); text-align:right;`;
/* columna de precio+quitar — en móvil ocupa ambas columnas en segunda fila */
const ItemRight = styled.div`
  text-align:right;
  @media(max-width:500px){
    grid-column:1/-1;
    display:flex; justify-content:space-between; align-items:center; flex-direction:row-reverse;
  }
`;

const QtyRow = styled.div`display:flex; align-items:center; gap:8px; margin-top:10px;`;
const QtyBtn = styled.button`
  width:30px; height:30px; border-radius:7px; border:1px solid rgba(26,18,8,.12);
  font-size:1.1rem; display:flex; align-items:center; justify-content:center; cursor:pointer;
  &:hover{border-color:#7C3A00;}
`;
const QtyInput = styled.input`
  width:44px; text-align:center; border:1px solid rgba(26,18,8,.12); border-radius:7px;
  padding:5px 2px; font-size:.9rem; color:#1A1208;
`;
const RemoveBtn = styled.button`
  align-self:flex-start; margin-top:4px; background:none; border:none; cursor:pointer;
  color:rgba(26,18,8,.3); &:hover{color:#c0392b;}
  @media(max-width:500px){ margin-top:0; }
`;

/* Summary card */
const Summary = styled.div`
  background:#1A1208; border-radius:20px; padding:32px; color:#FFFDF7; position:sticky; top:110px;
  @media(max-width:860px){ position:static; }
  @media(max-width:500px){ padding:24px 18px; border-radius:16px; }
`;
const SummaryRow = styled.div`display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;`;
const SummaryLabel = styled.span`font-size:.85rem; opacity:.65;`;
const SummaryValue = styled.span`font-weight:600;`;
const Total = styled.div`
  display:flex; justify-content:space-between; align-items:baseline;
  border-top:1px solid rgba(255,253,247,.12); margin:20px 0; padding-top:20px;
  font-family:'Cormorant Garamond',serif; font-size:1.5rem;
`;
const CheckoutBtn = styled.button`
  display:flex; align-items:center; justify-content:center; gap:10px;
  width:100%; padding:16px; border-radius:12px; background:#C07020; color:#FFFDF7;
  font-size:.88rem; font-weight:700; letter-spacing:.08em; border:none; cursor:pointer;
  margin-top:8px; transition:background .2s, transform .2s;
  &:hover:not(:disabled){background:#FAB000; transform:translateY(-2px);}
  &:disabled{opacity:.65; cursor:not-allowed;}
`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const SpinIcon = styled(Loader)`animation:${spin} .8s linear infinite;`;
const ClearBtn = styled.button`
  width:100%; padding:11px; border-radius:10px; background:transparent;
  border:1px solid rgba(255,253,247,.18); color:rgba(255,253,247,.55);
  font-size:.8rem; cursor:pointer; margin-top:10px;
  &:hover{border-color:rgba(255,253,247,.5); color:#FFFDF7;}
`;
const TermsRow = styled.label`
  display:flex; align-items:flex-start; gap:10px; margin-top:16px; cursor:pointer;
`;
const TermsCheck = styled.input`
  margin-top:2px; flex-shrink:0; width:16px; height:16px; accent-color:#C07020; cursor:pointer;
`;
const TermsText = styled.span`
  font-size:.76rem; color:rgba(255,253,247,.6); line-height:1.5;
  a{ color:#C07020; text-decoration:underline; }
  a:hover{ color:#FAB000; }
`;

/* Empty state */
const Empty = styled.div`text-align:center; padding:80px 0; @media(max-width:500px){padding:48px 0;}`;
const EmptyIcon = styled.div`font-size:4rem; margin-bottom:24px;`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}`;
const FadeIn = styled.div`animation:${fadeIn} .5s ease;`;

export default function CartPage() {
  const { items, removeFromCart, updateQty, clearCart } = useCart();
  const { lang } = useLanguage();
  const { region, symbol: currSym } = useRegion();
  const { checkout, loading: checkoutLoading, error: checkoutError } = useCheckout();
  const [allSkus, setAllSkus] = useState([]);
  const [selectedRate,        setSelectedRate]        = useState(null);
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [deliveryAddress,     setDeliveryAddress]     = useState(null);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAllSkus(data); })
      .catch(() => {});
  }, []);

  const enriched = items.map(item => {
    const sku = allSkus.find(s => String(s.id) === String(item.id));
    return sku ? { ...item, sku } : null;
  }).filter(Boolean);

  const isMxn = region === 'mx';

  const getPrice    = (sku) => isMxn ? (sku.priceMxn ?? sku.price) : sku.price;
  const subtotal    = enriched.reduce((acc, i) => acc + (getPrice(i.sku) * i.qty), 0);

  // Peso y dimensiones reales del pedido (usa datos del producto si existen)
  const totalWeightKg = enriched.reduce((acc, i) => acc + ((i.sku.weightKg || 0.7) * i.qty), 0) || 0.7;
  const totalUnits    = enriched.reduce((acc, i) => acc + i.qty, 0) || 1;
  // Dimensiones acumuladas: apilamos unidades verticalmente (alto × unidades), ancho y largo del mayor ítem
  const maxLengthCm = Math.max(...enriched.map(i => i.sku.lengthCm || 20));
  const maxWidthCm  = Math.max(...enriched.map(i => i.sku.widthCm  || 15));
  const totalHeightCm = enriched.reduce((acc, i) => acc + ((i.sku.heightCm || 10) * i.qty), 0) || 10;
  const packageDims = { lengthCm: maxLengthCm, widthCm: maxWidthCm, heightCm: totalHeightCm };
  const shippingCost  = selectedRate ? parseFloat(selectedRate.amount) : 0;
  const total         = subtotal + shippingCost;

  const [addrValidating] = useState(false); // ya no se usa — cotización actúa como validación
  const [termsAccepted, setTermsAccepted] = useState(false);

  function handleCheckout() {
    const addrError = validateAddress(deliveryAddress, lang);
    if (addrError) { alert(addrError); return; }

    const stripeItems  = enriched.map(i => ({ id: i.sku.id, quantity: i.qty }));
    const shippingInfo = selectedRate ? {
      rate_id:          selectedRate.id,
      quotation_id:     selectedQuotationId,
      carrier_name:     selectedRate.carrier_name || selectedRate.carrier,
      shipping_address: deliveryAddress,
    } : {};
    checkout(stripeItems, shippingInfo);
  }

  const t = {
    title:    lang==='es' ? 'Carrito de compras' : 'Shopping Cart',
    empty:    lang==='es' ? 'Tu carrito está vacío' : 'Your cart is empty',
    emptySub: lang==='es' ? 'Descubre nuestra selección de cajetas artesanales' : 'Discover our artisanal cajeta selection',
    shop:     lang==='es' ? 'Ir a la tienda' : 'Shop now',
    subtotal: lang==='es' ? 'Subtotal' : 'Subtotal',
    total:    lang==='es' ? 'Total' : 'Total',
    shipping: lang==='es' ? 'Envío' : 'Shipping',
    calcShip: lang==='es' ? 'Calcula tu envío abajo' : 'Calculate shipping below',
    checkout: lang==='es' ? 'Proceder a pagar' : 'Proceed to checkout',
    clear:    lang==='es' ? 'Vaciar carrito' : 'Clear cart',
    back:     lang==='es' ? 'Seguir comprando' : 'Continue shopping',
    terms:    lang==='es' ? 'He leído y acepto los' : 'I have read and accept the',
    termsLink:lang==='es' ? 'Términos y Condiciones' : 'Terms and Conditions',
    and:      lang==='es' ? 'y el' : 'and the',
    privLink: lang==='es' ? 'Aviso de Privacidad' : 'Privacy Notice',
    termsReq: lang==='es' ? 'Debes aceptar los términos para continuar' : 'You must accept the terms to continue',
  };

  if (enriched.length === 0) {
    return (
      <Page><Wrap>
        <FadeIn>
          <Empty>
            <EmptyIcon>🫙</EmptyIcon>
            <H1>{t.empty}</H1>
            <p style={{color:'rgba(26,18,8,.5)', marginBottom:32}}>{t.emptySub}</p>
            <Link to={`/${region}/store`} style={{
              display:'inline-flex', alignItems:'center', gap:8, padding:'14px 28px',
              background:'#7C3A00', color:'#FFFDF7', borderRadius:12, textDecoration:'none',
              fontWeight:700, fontSize:'.9rem'
            }}><ShoppingBag size={16}/>{t.shop}</Link>
          </Empty>
        </FadeIn>
      </Wrap></Page>
    );
  }

  return (
    <Page>
      <Wrap>
        <Header>
          <ShoppingBag size={26} color="#7C3A00" />
          <div>
            <Eyebrow>HACIENDA REAL</Eyebrow>
            <H1>{t.title}</H1>
          </div>
        </Header>

        <Layout>
          {/* Items list */}
          <div>
            {enriched.map(item => {
              const info = item.sku[lang] || item.sku.en;
              return (
                <ItemRow key={item.id}>
                  <ItemImg>
                    {item.sku.image
                      ? <img src={item.sku.image} alt={info.name} />
                      : <div style={{fontSize:32}}>🫙</div>
                    }
                  </ItemImg>

                  <div>
                    <ItemName to={`/${region}/product/${item.id}`}>{info.name}</ItemName>
                    <br />
                    <TypePill $v={item.sku.v}>{item.sku.v === 'envinada' ? (lang==='es'?'Envinada':'Envinada') : (lang==='es'?'Natural':'Natural')}</TypePill>
                    <QtyRow>
                      <QtyBtn onClick={()=>updateQty(item.id, Math.max(1, item.qty-1))}>−</QtyBtn>
                      <QtyInput
                        type="number" min={1} value={item.qty}
                        onChange={e=>updateQty(item.id, Math.max(1, Number(e.target.value)))}
                      />
                      <QtyBtn onClick={()=>updateQty(item.id, item.qty+1)}>+</QtyBtn>
                    </QtyRow>
                  </div>

                  <ItemRight>
                    <RemoveBtn onClick={()=>removeFromCart(item.id)}><X size={16}/></RemoveBtn>
                    {getPrice(item.sku) > 0 && <>
                      <ItemPrice>{currSym}{getPrice(item.sku).toFixed(2)}</ItemPrice>
                      <ItemSubtotal>× {item.qty} = {currSym}{(getPrice(item.sku)*item.qty).toFixed(2)}</ItemSubtotal>
                    </>}
                  </ItemRight>
                </ItemRow>
              );
            })}

            <div style={{marginTop:28}}>
              <Link to={`/${region}/store`} style={{
                display:'inline-flex', alignItems:'center', gap:6,
                color:'rgba(26,18,8,.45)', fontSize:'.85rem', textDecoration:'none'
              }}><ArrowLeft size={14}/>{t.back}</Link>
            </div>

            {/* Dirección + colonias SEPOMEX + verificación Shippo + tarifas */}
            <AddressForm
              region={region}
              lang={lang}
              totalWeightKg={totalWeightKg}
              lengthCm={packageDims.lengthCm}
              widthCm={packageDims.widthCm}
              heightCm={packageDims.heightCm}
              onChange={addr => {
                setDeliveryAddress(addr);
                setSelectedRate(null);
                setSelectedQuotationId(null);
              }}
              onRateSelect={(rate, quotationId) => {
                setSelectedRate(rate);
                setSelectedQuotationId(quotationId);
              }}
            />
          </div>

          {/* Order summary */}
          <Summary>
            <p style={{fontFamily:'Raleway,serif',letterSpacing:'.15em',fontSize:'.65rem',opacity:.55,marginBottom:20}}>
              {lang==='es' ? 'RESUMEN DE PEDIDO' : 'ORDER SUMMARY'}
            </p>
            <SummaryRow>
              <SummaryLabel>{t.subtotal}</SummaryLabel>
              <SummaryValue>{subtotal>0?`${currSym}${subtotal.toFixed(2)}`:'-'}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>{t.shipping}</SummaryLabel>
              <SummaryValue style={selectedRate ? {} : {fontSize:'.78rem',opacity:.55}}>
                {selectedRate
                  ? `${currSym}${shippingCost.toFixed(2)}`
                  : t.calcShip}
              </SummaryValue>
            </SummaryRow>
            {selectedRate && (
              <p style={{fontSize:'.72rem', opacity:.5, marginTop:-10, marginBottom:12, textAlign:'right'}}>
                {selectedRate.carrier} · {selectedRate.service}
                {selectedRate.delivery_days ? ` · ${selectedRate.delivery_days}–${selectedRate.delivery_days+1} ${lang==='es'?'días hábiles':'business days'}` : ''}
              </p>
            )}
            <Total>
              <span>{t.total}</span>
              <span style={{color:'#FAB000'}}>{total>0?`${currSym}${total.toFixed(2)}`:'-'}</span>
            </Total>
            <TermsRow>
              <TermsCheck
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
              />
              <TermsText>
                {t.terms}{' '}
                <a href={`/${region}/terms`} target="_blank" rel="noopener noreferrer">
                  {t.termsLink}
                </a>
                {' '}{t.and}{' '}
                <a href={`/${region}/privacy`} target="_blank" rel="noopener noreferrer">
                  {t.privLink}
                </a>
              </TermsText>
            </TermsRow>
            <CheckoutBtn
              onClick={handleCheckout}
              disabled={checkoutLoading || addrValidating || subtotal === 0 || !selectedRate || !selectedQuotationId || !deliveryAddress?.name || !termsAccepted}>
              {addrValidating
                ? <><SpinIcon size={16}/> {lang==='es'?'Validando dirección\u2026':'Validating address\u2026'}</>
                : checkoutLoading
                  ? <><SpinIcon size={16}/> {lang==='es'?'Redirigiendo\u2026':'Redirecting\u2026'}</>
                  : <>{t.checkout} <ChevronRight size={16}/></>}
            </CheckoutBtn>
            {!termsAccepted && selectedRate && deliveryAddress?.name && (
              <p style={{color:'rgba(255,253,247,0.5)', fontSize:'.76rem', marginTop:8, textAlign:'center'}}>
                {t.termsReq}
              </p>
            )}
            {!selectedRate && subtotal > 0 && (
              <p style={{color:'rgba(255,253,247,0.5)', fontSize:'.76rem', marginTop:8, textAlign:'center'}}>
                {lang==='es' ? '⬅ Selecciona una opción de envío para continuar' : '⬅ Select a shipping option to continue'}
              </p>
            )}
            {selectedRate && !deliveryAddress?.name && (
              <p style={{color:'rgba(255,253,247,0.5)', fontSize:'.76rem', marginTop:8, textAlign:'center'}}>
                {lang==='es' ? '⬅ Completa tu dirección de entrega' : '⬅ Fill in your delivery address'}
              </p>
            )}
            {checkoutError && (
              <p style={{color:'#E05252', fontSize:'.75rem', marginTop:8, textAlign:'center'}}>
                {checkoutError}
              </p>
            )}
            <ClearBtn onClick={clearCart}>{t.clear}</ClearBtn>
          </Summary>
        </Layout>
      </Wrap>
    </Page>
  );
}
